import { useEffect, useState } from 'react';
import { AlertTriangle, CreditCard, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router';

/**
 * Paid-subscription state banner (EIP-322).
 *
 * The sibling TrialBanner is hard-gated on `subscription_status === 'trial'`, so a paid
 * tenant could never see it — which is why an elapsed paid subscription used to surface
 * as literally one amber pixel on the billing page and nothing else.
 *
 * Renders server-supplied state verbatim. It does NOT compare dates to the browser
 * clock: the boundary is UTC, `toLocaleDateString` renders in the viewer's timezone, and
 * a client that decides its own entitlement will eventually disagree with the server's
 * 403. The backend decides whether; this only decides what to say about it.
 */
export function SubscriptionBanner() {
  const { tenant, hasRole, isSuperadmin, viewingAsSuperadmin } = useAuth();
  const navigate = useNavigate();

  // A 403 from the API is fresher than the tenant blob cached at login, which is
  // refreshed from only two call sites and never on mount. Without this, a tenant that
  // crossed the boundary mid-session would be getting 403s with no banner explaining
  // them until they logged out and back in.
  const [blockedByServer, setBlockedByServer] = useState(false);
  useEffect(() => {
    const onExpired = () => setBlockedByServer(true);
    window.addEventListener('eva:subscription-expired', onExpired);
    return () => window.removeEventListener('eva:subscription-expired', onExpired);
  }, []);

  // Server verdict wins when the two disagree.
  const state = blockedByServer ? 'expired' : tenant?.subscription_state;

  if (state !== 'grace' && state !== 'expired') {
    return null;
  }

  // A superadmin inspecting a tenant is not that tenant's subscriber.
  if (isSuperadmin && viewingAsSuperadmin) {
    return null;
  }

  // Trials have their own banner with its own copy and CTA.
  if (tenant?.subscription_status === 'trial') {
    return null;
  }

  const isExpired = state === 'expired';
  const isAdmin = hasRole(['admin']);

  // EIP-318: the normalized status (evaluate_subscription_access's own "status" key,
  // surfaced via TenantInfo.subscription_normalized_status) lets this banner name
  // suspended/terminated/cancelled instead of one generic "ended" message. Absent on
  // a stale cached tenant blob (pre-EIP-318 login) — that case, and any status this
  // banner doesn't special-case, falls through to the original generic copy verbatim.
  const normalizedStatus = tenant?.subscription_normalized_status;
  const isSuspended = normalizedStatus === 'suspended';
  const isTerminated = normalizedStatus === 'terminated';
  const isCancelled = normalizedStatus === 'cancelled';

  let graceEndDate = '';
  if (tenant?.subscription_grace_ends_at) {
    try {
      graceEndDate = new Date(tenant.subscription_grace_ends_at).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (_e) {
      // A malformed date just means we omit it — never a reason to hide the warning.
    }
  }

  return (
    <div
      className={`px-4 py-2.5 flex items-center justify-between text-sm ${
        isExpired ? 'bg-destructive text-destructive-foreground' : 'bg-amber-500 text-white'
      }`}
      data-testid="subscription-banner"
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-4 w-4" />
        {isExpired ? (
          isSuspended ? (
            <span>
              Your subscription is <strong>suspended</strong>. You can still view and export
              your data, but changes and AI features are paused
              {isAdmin ? '. Contact support to restore it.' : '. Please contact your administrator.'}
            </span>
          ) : isTerminated ? (
            <span>
              Your subscription was <strong>terminated</strong>. You can still view and export
              your data, but changes and AI features are paused
              {isAdmin ? '. Contact support if you think this is a mistake.' : '. Please contact your administrator.'}
            </span>
          ) : (
            <span>
              Your subscription has <strong>ended</strong>. You can still view and export your
              data, but changes and AI features are paused
              {isAdmin ? ' until you renew.' : '. Please contact your administrator.'}
            </span>
          )
        ) : isCancelled ? (
          <span>
            Your subscription was <strong>cancelled</strong>.
            {graceEndDate
              ? ` Access continues until ${graceEndDate}, after which changes and AI features will pause.`
              : ' Access will be limited shortly.'}
          </span>
        ) : (
          <span>
            Your subscription has <strong>ended</strong>.
            {graceEndDate
              ? ` Access continues until ${graceEndDate}, after which changes and AI features will pause.`
              : ' Access will be limited shortly.'}
          </span>
        )}
      </div>

      {isAdmin ? (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => navigate('/settings/billing')}
          className="h-7 px-3 bg-white/20 hover:bg-white/30 text-white border-0"
          data-testid="subscription-renew-btn"
        >
          <CreditCard className="h-3 w-3 mr-1.5" />
          {isExpired ? 'Renew Now' : 'Manage Plan'}
        </Button>
      ) : (
        <div className="flex items-center gap-2 text-xs opacity-80">
          <Mail className="h-3 w-3" />
          <span>Contact your admin to renew</span>
        </div>
      )}
    </div>
  );
}
